/**
 * SH ProductWiz options — pills, font tiles and swatch-row scroll hints
 *
 * Native port of the WW-0008 variation's ProductWiz ("RIO") behaviour, from
 * assets/www-0001-it2.js. Companion to assets/sh-rio-options.css.
 *
 * ProductWiz owns the option UI inside .rio-options and renders it with
 * React/MUI, so none of this can be done in Liquid — the markup does not
 * exist until the app mounts. Three independent things happen here:
 *
 *   1. Swatch rows get a right-edge fade when there is more to scroll.
 *   2. Select options with few enough values become pill rows. Options with
 *      more values keep the app's dropdown, restyled in CSS.
 *   3. Schriftart-style options become image tiles.
 *
 * Nothing is ever rewritten inside .rio-options. The replaced groups are
 * hidden and our own hosts are appended after the app, which is what keeps
 * this working across the app's re-renders. Ordering back into place is the
 * CSS file's job (.sh-rio-order).
 *
 * A pill cannot simply set a value — the app owns the state. activateValue()
 * escalates through four strategies, stopping at the first that takes:
 *   1. swap the Shopify variant on the form directly
 *   2. set a native <select>, or call React's onChange through the fiber
 *   3. click an option node already rendered inside the group
 *   4. open the MUI menu invisibly, click the item, force it closed
 *
 * Availability is read from the app's own painted state first, then checked
 * against the Shopify variant matrix, so sold-out marks survive a rebuild.
 *
 * Configuration comes from the .sh-rio-config marker that
 * snippets/sh-rio-options.liquid prints. Without it, nothing runs.
 */
(function () {
  'use strict';

  var SECTION = '.shopify-section--main-product';
  var RIO = '.rio-options';
  var PILLS = 'sh-rio-pills';
  var FONTS = 'sh-rio-fonts';
  var ROW = 'sh-rio-row';
  var OPT_ATTR = 'data-sh-rio-option';
  var VALUE_ATTR = 'data-sh-value';
  var HIDDEN_ATTR = 'data-sh-rio-native-hidden';
  var HIDDEN_CLASS = 'sh-rio-native-hidden';
  var TITLE_SEL =
    '.rio-product-option-title, [class*="option-title"], [class*="OptionTitle"]';

  if (window.__shRioInit) return;

  var config = readConfig();
  if (!config) return;

  window.__shRioInit = true;

  /**
   * The marker is printed by the section, so its absence means the feature is
   * off and every selector in the stylesheet stays inert.
   */
  function readConfig() {
    var el = document.querySelector('.sh-rio-config');
    if (!el) return null;

    function flag(name, fallback) {
      var raw = el.getAttribute('data-' + name);
      if (raw === null || raw === '') return fallback;
      return raw === 'true';
    }

    return {
      pills: flag('pills', true),
      fonts: flag('fonts', true),
      hints: flag('hints', true),
      order: flag('order', true),
      typo: flag('typo', true),
      tight: flag('tight', true),
      hideArrows: flag('hide-arrows', true),
      maxValues: parseInt(el.getAttribute('data-max-values'), 10) || 4,
    };
  }

  function applyBodyClasses() {
    var body = document.body;
    body.classList.add('sh-rio-on');
    if (config.order) body.classList.add('sh-rio-order');
    if (config.typo) body.classList.add('sh-rio-typo');
    if (config.tight) body.classList.add('sh-rio-tight');
    if (config.hideArrows) body.classList.add('sh-rio-hide-arrows');
  }

  /* =======================================================================
     Small helpers
     ======================================================================= */

  var rioFeaturesReady = { pills: !config.pills, fonts: !config.fonts };

  function stopRioMountWatch(section) {
    section = section || document.querySelector(SECTION);
    if (!section || !section.__shRioMountObserver) return;
    section.__shRioMountObserver.disconnect();
    section.__shRioMountObserver = null;
  }

  function markRioReady() {
    var section = document.querySelector(SECTION);
    if (document.documentElement.classList.contains('sh-rio-ready')) {
      stopRioMountWatch();
      watchRioInner(section);
      return;
    }
    document.documentElement.classList.add('sh-rio-ready');
    document.documentElement.classList.remove('sh-rio-pending');
    // Drop the form-wide subtree watcher. Second (and later) conversions
    // still run: watchRioInner on .rio-options, watchFormOrder if that node
    // is replaced, and variant:changed.
    stopRioMountWatch();
    watchRioInner(section);
    syncAppSwatches(section);
  }

  function markFeatureReady(name) {
    rioFeaturesReady[name] = true;
    var section = document.querySelector(SECTION);
    // Do not treat "poll timed out, app never mounted" as done — that would
    // disconnect the watcher and leave a late .rio-options unconverted.
    if (!section || !section.querySelector(RIO)) return;
    if (!rioFeaturesReady.pills || !rioFeaturesReady.fonts) return;
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(markRioReady);
    });
  }

  /** Calls tryFn until it returns true or maxTries is reached. */
  function pollUntil(tryFn, options) {
    options = options || {};
    var interval = options.interval || 50;
    var maxTries = options.maxTries || 80;
    var onDone = options.onDone;

    if (tryFn()) {
      if (onDone) onDone();
      return;
    }

    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      var ok = tryFn();
      if (ok || tries >= maxTries) {
        clearInterval(timer);
        if (ok && onDone) onDone();
      }
    }, interval);
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalizeLabel(text) {
    return String(text || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /** Folds ß/ss so "GROSS" and "Groß" compare equal. */
  function foldLabel(text) {
    return normalizeLabel(text)
      .replace(/ß/g, 'ss')
      .replace(/schriftart/g, 'schrift');
  }

  function valuesEqual(a, b) {
    var x = foldLabel(a);
    var y = foldLabel(b);
    return !!x && x === y;
  }

  /**
   * Picks the single best candidate for a label. Exact match always wins.
   * Containment is only used when the app's labels and Shopify's have drifted
   * and the answer is still unambiguous — nested siblings such as
   * "klein Weißblech" and "nur Deckel klein Weißblech" must never alias.
   */
  function pickBestValue(candidates, target) {
    if (!target || !candidates || !candidates.length) return '';
    var needle = foldLabel(target);
    if (!needle) return '';

    var list = [];
    var i;
    var c;
    var n;
    for (i = 0; i < candidates.length; i++) {
      c = String(candidates[i] || '')
        .replace(/\s+/g, ' ')
        .trim();
      if (!c) continue;
      list.push(c);
      if (foldLabel(c) === needle) return c;
    }
    if (!list.length) return '';

    var fuzzy = [];
    for (i = 0; i < list.length; i++) {
      n = foldLabel(list[i]);
      if (n.indexOf(needle) !== -1 || needle.indexOf(n) !== -1) fuzzy.push(list[i]);
    }
    if (fuzzy.length === 1) return fuzzy[0];
    if (fuzzy.length > 1) {
      fuzzy.sort(function (x, y) {
        var dx = Math.abs(foldLabel(x).length - needle.length);
        var dy = Math.abs(foldLabel(y).length - needle.length);
        if (dx !== dy) return dx - dy;
        return foldLabel(x).length - foldLabel(y).length;
      });
      var bestDiff = Math.abs(foldLabel(fuzzy[0]).length - needle.length);
      var ties = 0;
      for (i = 0; i < fuzzy.length; i++) {
        if (Math.abs(foldLabel(fuzzy[i]).length - needle.length) === bestDiff) ties += 1;
      }
      if (ties === 1) return fuzzy[0];
    }

    // Token subset, again only when exactly one candidate qualifies.
    var needleTokens = needle.split(/[^a-z0-9+]+/).filter(Boolean);
    var tokenHits = [];
    for (i = 0; i < list.length; i++) {
      var candTokens = foldLabel(list[i])
        .split(/[^a-z0-9+]+/)
        .filter(Boolean);
      if (needleTokens.length < 2 || candTokens.length < 2) continue;
      var shorter = needleTokens.length <= candTokens.length ? needleTokens : candTokens;
      var longer = needleTokens.length <= candTokens.length ? candTokens : needleTokens;
      var ok = true;
      var t;
      for (t = 0; t < shorter.length; t++) {
        if (longer.indexOf(shorter[t]) === -1) {
          ok = false;
          break;
        }
      }
      if (ok) tokenHits.push(list[i]);
    }
    if (tokenHits.length === 1) return tokenHits[0];
    return '';
  }

  function isOurNode(el) {
    return !!(el && el.closest && el.closest('.' + PILLS + ', .' + FONTS));
  }

  /* =======================================================================
     Data model — the app's option config and the Shopify variant matrix
     ======================================================================= */

  function getOptionsData() {
    return (
      window.__productWizRioProductOptions ||
      (window.__productWizRio && window.__productWizRio.productOptions) ||
      null
    );
  }

  /**
   * Availability is only as good as the variant list behind it.
   * snippets/ww-full-variants.liquid merges the overflow variants of products
   * past Shopify's 250 cap into the app's product object, while the .js
   * endpoint below is capped, so whichever list is longer is the right one to
   * trust. Taking the fetched copy unconditionally would mark variants of the
   * large personalised products sold out.
   */
  function getProductData() {
    var fetched = window.__shRioProductJson;
    var app = window.__productWizRioProduct;
    var fetchedCount = fetched && Array.isArray(fetched.variants) ? fetched.variants.length : 0;
    var appCount = app && Array.isArray(app.variants) ? app.variants.length : 0;
    if (fetchedCount && fetchedCount >= appCount) return fetched;
    if (appCount) return app;
    return fetched || app || null;
  }

  function ensureProductJson() {
    if (window.__shRioProductJson || window.__shRioProductJsonLoading) return;
    if (window.location.pathname.indexOf('/products/') === -1) return;
    window.__shRioProductJsonLoading = true;

    fetch(window.location.pathname.replace(/\/$/, '') + '.js', {
      credentials: 'same-origin',
    })
      .then(function (res) {
        if (!res.ok) throw new Error('product.js');
        return res.json();
      })
      .then(function (json) {
        window.__shRioProductJson = json;
        var section = document.querySelector(SECTION);
        if (!section) return;
        placePills(section);
        syncFontTiles(section);
        ensureNativeGroupsHidden(section);
      })
      .catch(function () {
        window.__shRioProductJsonLoading = false;
      });
  }

  function findShopifyOptionIndex(optionName) {
    var product = getProductData();
    if (!product || !optionName) return -1;
    var names = (product.options || []).map(function (o) {
      return typeof o === 'string' ? o : o && o.name;
    });
    var i;
    for (i = 0; i < names.length; i++) {
      if (
        normalizeLabel(names[i]) === normalizeLabel(optionName) ||
        titleMatchesOption(names[i], optionName) ||
        titleMatchesOption(optionName, names[i])
      ) {
        return i;
      }
    }
    return -1;
  }

  /** Shopify's option order is canonical; the app often lists them differently. */
  function shopifySortIndex(optionName) {
    var product = getProductData();
    var names = (product && product.options) || [];
    var target = normalizeLabel(optionName);
    var i;
    for (i = 0; i < names.length; i++) {
      var name = typeof names[i] === 'string' ? names[i] : names[i] && names[i].name;
      if (normalizeLabel(name) === target) return i;
    }
    return 999;
  }

  function sortByShopifyOrder(options) {
    return options.slice().sort(function (a, b) {
      return shopifySortIndex(a.name || a.id || '') - shopifySortIndex(b.name || b.id || '');
    });
  }

  /**
   * Canonical values for an option. The app's configured values are often
   * stale leftovers, so the live controls and the variant matrix win.
   */
  function getCanonicalValues(optionName, section, fallbackValues) {
    var values = [];
    var seen = {};

    function pushUnique(raw) {
      var v = String(raw || '')
        .replace(/\s+/g, ' ')
        .trim();
      if (!v) return;
      var key = normalizeLabel(v);
      if (seen[key]) return;
      seen[key] = true;
      values.push(v);
    }

    if (section && optionName) {
      var rio = section.querySelector(RIO) || document.querySelector(RIO);
      var group = rio && findOptionGroup(rio, optionName);
      var scopes = groupScopes(group);
      var s;
      for (s = 0; s < scopes.length; s++) {
        Array.prototype.forEach.call(scopes[s].querySelectorAll('select option'), function (opt) {
          var text = String(opt.text || opt.value || '')
            .replace(/\s+/g, ' ')
            .trim();
          if (!text || /^select/i.test(text)) return;
          pushUnique(text);
        });
      }
      if (values.length) return values;
    }

    var product = getProductData();
    if (product && optionName) {
      var idx = findShopifyOptionIndex(optionName);
      if (idx >= 0) {
        var def = product.options[idx];
        if (def && Array.isArray(def.values)) {
          def.values.forEach(function (v) {
            pushUnique(typeof v === 'string' ? v : v && v.name);
          });
        }
        if (Array.isArray(product.variants)) {
          var i;
          for (i = 0; i < product.variants.length; i++) {
            pushUnique(product.variants[i]['option' + (idx + 1)]);
          }
        }
      }
    }

    if (!values.length && Array.isArray(fallbackValues)) {
      fallbackValues.forEach(function (v) {
        pushUnique(typeof v === 'string' ? v : (v && (v.name || v.id)) || '');
      });
    }

    return values;
  }

  /* =======================================================================
     Finding an option's DOM group
     ======================================================================= */

  function titleMatchesOption(titleText, optionName) {
    var title = normalizeLabel(titleText);
    var target = normalizeLabel(optionName);
    if (!title || !target) return false;
    // Titles carry the selection: "Größe: klein". Option names on this store
    // often already end with a colon ("Wähle deine Schriftart:"), and ProductWiz
    // prints that name as the title. Splitting the title drops the colon, so
    // the name has to be compared without it or the native row is never hidden.
    var targetName = target.replace(/:+$/, '').trim() || target;
    var titleName = title.split(':')[0].replace(/\s+/g, ' ').trim();
    if (titleName === target || titleName === targetName) return true;
    if (
      title.indexOf(target + ':') === 0 ||
      title.indexOf(target + ' :') === 0 ||
      title.indexOf(targetName + ':') === 0 ||
      title.indexOf(targetName + ' :') === 0
    ) {
      return true;
    }
    if (titleName.length >= 5 && targetName.length >= 5) {
      if (titleName.slice(-targetName.length) === targetName) return true;
      if (targetName.slice(-titleName.length) === titleName) return true;
    }
    return false;
  }

  function findOptionGroup(rio, optionName) {
    if (!rio || !optionName) return null;
    var titles = rio.querySelectorAll(TITLE_SEL);
    var i;
    for (i = 0; i < titles.length; i++) {
      if (!titleMatchesOption(titles[i].textContent || '', optionName)) continue;

      // Largest ancestor that still belongs to this option alone.
      var el = titles[i];
      var best = el.parentElement || el;
      while (el && el !== rio) {
        var titleCount = el.querySelectorAll(TITLE_SEL).length;
        if (titleCount > 1) break;
        if (titleCount === 1) best = el;
        el = el.parentElement;
      }
      return best;
    }
    return null;
  }

  /**
   * The app splits a label and its value row across adjacent grid items, so
   * an option is its hide root plus every sibling up to the next title.
   */
  function groupScopes(groupRoot) {
    if (!groupRoot) return [];
    var start = getHideRoot(groupRoot) || groupRoot;
    var scopes = [start];
    var sib = start.nextElementSibling;
    while (sib) {
      if (sib.querySelector && sib.querySelector(TITLE_SEL)) break;
      scopes.push(sib);
      sib = sib.nextElementSibling;
    }
    return scopes;
  }

  function getHideRoot(groupRoot) {
    if (!groupRoot) return null;
    var rio = (groupRoot.closest && groupRoot.closest(RIO)) || groupRoot.parentElement;
    var el = groupRoot;
    while (el && el !== rio) {
      var cls = el.className ? String(el.className) : '';
      if (/Grid-item|MuiGrid-item/i.test(cls)) {
        if (el.querySelectorAll(TITLE_SEL).length <= 1) return el;
      }
      el = el.parentElement;
    }
    return groupRoot;
  }

  /* =======================================================================
     Deciding which options we take over
     ======================================================================= */

  function isDropdownMenuItem(el) {
    if (!el || !el.closest) return false;
    if (
      el.closest(
        '[role="listbox"], .MuiMenu-list, .MuiMenu-root, .MuiPopover-root, .MuiModal-root, .MuiPopper-root'
      )
    ) {
      return true;
    }
    try {
      var st = window.getComputedStyle(el);
      if (st.display === 'none' || st.visibility === 'hidden' || st.opacity === '0') return true;
    } catch (err) {}
    return false;
  }

  function isSelectTrigger(el) {
    if (!el) return false;
    if (el.getAttribute('role') === 'combobox' || el.getAttribute('aria-haspopup') === 'listbox') {
      return true;
    }
    if (
      el.closest &&
      el.closest('.MuiSelect-root, [class*="MuiSelect-root"], [class*="PwzrSelect"]') &&
      el.getAttribute('role') !== 'option' &&
      el.getAttribute('role') !== 'radio'
    ) {
      return true;
    }
    return false;
  }

  /**
   * Chips already on screen. A closed dropdown shows one control, not a row,
   * which is how a real dropdown is told apart from a stale "select" config
   * on an option the app actually rendered as chips.
   */
  function countVisibleChips(groupRoot) {
    var scopes = groupScopes(groupRoot);
    var count = 0;
    var i;
    var n;
    for (i = 0; i < scopes.length; i++) {
      var nodes = scopes[i].querySelectorAll(
        'button, [role="button"], [role="option"], [role="radio"]'
      );
      for (n = 0; n < nodes.length; n++) {
        var el = nodes[n];
        if (isOurNode(el)) continue;
        if (isDropdownMenuItem(el)) continue;
        if (isSelectTrigger(el)) continue;
        var aria = (el.getAttribute('aria-label') || '').toLowerCase();
        if (aria.indexOf('scroll') === 0 || aria.indexOf('scroll ') !== -1) continue;
        count += 1;
      }
    }
    return count;
  }

  function looksLikeDropdown(groupRoot) {
    if (!groupRoot) return false;
    return countVisibleChips(groupRoot) < 2;
  }

  function countOptionValues(opt, section) {
    var name = (opt && (opt.name || opt.id)) || '';
    var canonical = getCanonicalValues(name, section, (opt && opt.values) || []);
    if (canonical.length) return canonical.length;
    return opt && Array.isArray(opt.values) ? opt.values.length : 0;
  }

  function isHiddenByUs(el) {
    while (el) {
      if (el.getAttribute && el.getAttribute(HIDDEN_ATTR) === '1') return true;
      if (el.classList && el.classList.contains(HIDDEN_CLASS)) return true;
      el = el.parentElement;
    }
    return false;
  }

  function isVisibleOption(group) {
    if (!group) return false;
    // Our own hiding must not read as "the app dropped this option".
    if (isHiddenByUs(group)) return true;
    try {
      var style = window.getComputedStyle(group);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
    } catch (err) {}
    return true;
  }

  function valueHasImage(val) {
    if (!val || typeof val !== 'object') return false;
    var cfg = val.config || {};
    var bg = cfg.backgroundImage;
    if (bg && typeof bg === 'object' && (bg.id || bg.url || bg.src)) return true;
    if (typeof bg === 'string' && bg.replace(/\s/g, '')) return true;
    var img = cfg.image || cfg.imageUrl || cfg.imageSrc || val.image || val.imageUrl || '';
    if (img && String(img).trim()) return true;
    return !!getFontValueMeta(val).img;
  }

  function optionHasAnyImage(opt) {
    var vals = (opt && opt.values) || [];
    var i;
    for (i = 0; i < vals.length; i++) {
      if (valueHasImage(vals[i])) return true;
    }
    return false;
  }

  /** Real images already painted in the group — never replace those with text. */
  function groupHasRenderedImages(groupRoot) {
    var scopes = groupScopes(groupRoot);
    var i;
    var n;
    for (i = 0; i < scopes.length; i++) {
      var imgs = scopes[i].querySelectorAll('img');
      for (n = 0; n < imgs.length; n++) {
        var src = imgs[n].getAttribute('src') || imgs[n].getAttribute('data-src') || '';
        if (!src) continue;
        if (/data:image\/svg|placeholder|blank\.|spacer/i.test(src)) continue;
        return true;
      }
      var painted = scopes[i].querySelectorAll('[style*="background-image"]');
      for (n = 0; n < painted.length; n++) {
        var bg = '';
        try {
          bg = window.getComputedStyle(painted[n]).backgroundImage || '';
        } catch (err) {}
        if (bg && bg !== 'none' && bg.indexOf('url(') !== -1) return true;
      }
    }
    return false;
  }

  function getSelectOptions(optionsData) {
    if (!optionsData || !Array.isArray(optionsData.options)) return [];
    return optionsData.options.filter(function (opt) {
      return (
        opt &&
        opt.config &&
        String(opt.config.type || '').toLowerCase() === 'select' &&
        Array.isArray(opt.values) &&
        opt.values.length > 0
      );
    });
  }

  /** Swatch/button rows the app renders without images become text pills. */
  function getTextChipOptions(optionsData, rio) {
    if (!optionsData || !Array.isArray(optionsData.options) || !rio) return [];
    return optionsData.options.filter(function (opt) {
      if (!opt || !Array.isArray(opt.values) || !opt.values.length) return false;
      if (isFontOption(opt)) return false;
      var type = String((opt.config && opt.config.type) || '').toLowerCase();
      var name = opt.name || opt.id || '';
      if (!name) return false;
      if (type !== 'swatch' && type !== 'button' && type !== 'select') return false;
      if (optionHasAnyImage(opt)) return false;
      var group = findOptionGroup(rio, name);
      if (!group || !isVisibleOption(group)) return false;
      if (groupHasRenderedImages(group)) return false;
      // Real dropdowns belong to the select path, not this one.
      if (type === 'select' && countVisibleChips(group) < 2) return false;
      return true;
    });
  }

  function getPillOptions(optionsData, rio) {
    var section =
      (rio && rio.closest && rio.closest(SECTION)) || document.querySelector(SECTION);
    var seen = {};
    var list = [];

    function pushUnique(opt) {
      if (!opt) return;
      var key = normalizeLabel(opt.name || opt.id || '');
      if (!key || seen[key]) return;
      seen[key] = true;
      list.push(opt);
    }

    getSelectOptions(optionsData).forEach(function (opt) {
      var name = opt.name || opt.id || '';
      if (!name || !rio) return;
      // Too many values for a pill row — the dropdown stays.
      if (countOptionValues(opt, section) > config.maxValues) return;
      var group = findOptionGroup(rio, name);
      if (!group || !isVisibleOption(group)) return;
      if (!looksLikeDropdown(group)) return;
      pushUnique(opt);
    });

    getTextChipOptions(optionsData, rio).forEach(pushUnique);
    return sortByShopifyOrder(list);
  }

  function stillWantsPills(optionName, optionsData, section) {
    if (!optionName || !optionsData || !Array.isArray(optionsData.options)) return false;
    var i;
    for (i = 0; i < optionsData.options.length; i++) {
      var opt = optionsData.options[i];
      var name = (opt && (opt.name || opt.id)) || '';
      if (
        normalizeLabel(name) !== normalizeLabel(optionName) &&
        !titleMatchesOption(name, optionName) &&
        !titleMatchesOption(optionName, name)
      ) {
        continue;
      }
      var type = String((opt.config && opt.config.type) || '').toLowerCase();
      if (type === 'select') return countOptionValues(opt, section) <= config.maxValues;
      if ((type === 'swatch' || type === 'button') && !isFontOption(opt) && !optionHasAnyImage(opt)) {
        return true;
      }
    }
    return false;
  }

  /* =======================================================================
     Font options
     ======================================================================= */

  /** Schriftart-style image tiles — not Motiv/Farbe swatches. */
  function isFontOption(opt) {
    if (!opt || !Array.isArray(opt.values) || !opt.values.length) return false;
    var type = String((opt.config && opt.config.type) || '').toLowerCase();
    if (type === 'select') return false;
    if (type && type !== 'swatch' && type !== 'button') return false;

    if (/schrift/i.test(String(opt.name || opt.id || ''))) return true;

    // Value-pattern fallback: Schriftart 1/2/3 plus an optional "ohne Name".
    if (opt.values.length < 2 || opt.values.length > 8) return false;
    var fontish = 0;
    var i;
    for (i = 0; i < opt.values.length; i++) {
      var n = String(opt.values[i].name || opt.values[i].id || '');
      if (/^schrift/i.test(n) || /^ohne\s*name$/i.test(n) || /^font/i.test(n)) fontish += 1;
    }
    return fontish >= Math.max(2, Math.ceil(opt.values.length * 0.6));
  }

  function getFontOptions(optionsData) {
    if (!optionsData || !Array.isArray(optionsData.options)) return [];
    return optionsData.options.filter(isFontOption);
  }

  function resolveAssetUrl(imageId) {
    if (!imageId) return '';
    var assets = window.__productWizRioAssets || [];
    var base = '';
    var i;
    for (i = 0; i < assets.length; i++) {
      if (assets[i] && assets[i].urlBase && assets[i].url) {
        base = assets[i].url;
        break;
      }
    }
    for (i = 0; i < assets.length; i++) {
      var a = assets[i];
      if (!a || a.id !== imageId) continue;
      return String(a.url || '')
        .replace('{0}', base)
        .replace('{i}', encodeURIComponent(imageId));
    }
    if (base) return base + '/' + encodeURIComponent(imageId);
    return '';
  }

  function getFontValueMeta(val) {
    var cfg = (val && val.config) || {};
    var imgId = cfg.backgroundImage && cfg.backgroundImage.id ? cfg.backgroundImage.id : '';
    return {
      name: (val && (val.name || val.id)) || '',
      img: resolveAssetUrl(imgId),
    };
  }

  function fontTilesSignature(fontOptions) {
    return (fontOptions || [])
      .map(function (opt) {
        var names = normalizeLabel((opt && (opt.name || opt.id)) || '');
        var imgs = (opt.values || [])
          .map(function (v) {
            return getFontValueMeta(v).img;
          })
          .join(',');
        return names + ':' + imgs;
      })
      .join('|');
  }

  function fontImagesResolved(fontOptions) {
    var needed = 0;
    var have = 0;
    (fontOptions || []).forEach(function (opt) {
      (opt.values || []).forEach(function (v) {
        var cfg = (v && v.config) || {};
        var imgId = cfg.backgroundImage && cfg.backgroundImage.id;
        if (!imgId) return;
        needed += 1;
        if (resolveAssetUrl(imgId)) have += 1;
      });
    });
    return !needed || have === needed;
  }

  function preloadFontImages(fontOptions) {
    (fontOptions || []).forEach(function (opt) {
      (opt.values || []).forEach(function (v) {
        var url = getFontValueMeta(v).img;
        if (!url) return;
        if (document.querySelector('link[rel="preload"][as="image"][href="' + url + '"]')) return;
        var link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = url;
        document.head.appendChild(link);
      });
    });
  }

  /* =======================================================================
     Reading the current selection
     ======================================================================= */

  function getForm(section) {
    return (
      (section &&
        (section.querySelector('form[is="product-form"]') ||
          section.querySelector('.product-form'))) ||
      document.querySelector('form[is="product-form"]')
    );
  }

  function getCurrentVariant(section) {
    var product = getProductData();
    if (!product || !Array.isArray(product.variants)) return null;

    var form = getForm(section);
    var idInput = form && form.querySelector('[name="id"]');
    var id = idInput && idInput.value ? String(idInput.value) : '';
    var i;

    if (id) {
      for (i = 0; i < product.variants.length; i++) {
        if (String(product.variants[i].id) === id) return product.variants[i];
      }
    }

    try {
      var fromUrl = new URLSearchParams(window.location.search).get('variant');
      if (fromUrl) {
        for (i = 0; i < product.variants.length; i++) {
          if (String(product.variants[i].id) === String(fromUrl)) return product.variants[i];
        }
      }
    } catch (err) {}

    return product.variants[0] || null;
  }

  function getSelectedValue(optionName, section) {
    var variant = getCurrentVariant(section);
    if (getProductData() && variant) {
      var idx = findShopifyOptionIndex(optionName);
      if (idx >= 0) {
        var fromVariant = String(variant['option' + (idx + 1)] || '');
        if (fromVariant) return fromVariant;
      }
    }
    return getSelectedValueFromDom(optionName, section);
  }

  function getSelectedValueFromDom(optionName, section) {
    var rio = (section && section.querySelector(RIO)) || document.querySelector(RIO);
    if (!rio) return '';
    var group = findOptionGroup(rio, optionName);
    if (!group) return '';

    var sel = group.querySelector('select');
    if (sel && sel.selectedIndex >= 0 && sel.options[sel.selectedIndex]) {
      return String(
        sel.options[sel.selectedIndex].text || sel.options[sel.selectedIndex].value || ''
      ).trim();
    }

    var active = group.querySelector(
      '[aria-selected="true"], [aria-checked="true"], .Mui-selected, [class*="selected"]'
    );
    if (active && !isOurNode(active)) {
      return String(
        active.getAttribute('aria-label') ||
          active.getAttribute('data-value') ||
          active.textContent ||
          ''
      )
        .replace(/\s+/g, ' ')
        .trim();
    }

    // The title often carries it: "Personalisierung: mit Name".
    var title = group.querySelector('.rio-product-option-title, [class*="option-title"]');
    if (title) {
      var parts = String(title.textContent || '').split(':');
      if (parts.length > 1) {
        return parts
          .slice(1)
          .join(':')
          .replace(/\s+/g, ' ')
          .trim();
      }
    }
    return '';
  }

  /** Maps one of our labels onto the closest real Shopify option value. */
  function resolveShopifyValue(optionName, valueName, section) {
    var product = getProductData();
    if (!product || !valueName) return valueName;

    var idx = findShopifyOptionIndex(optionName);
    var candidates = [];
    var i;
    if (idx >= 0 && Array.isArray(product.variants)) {
      for (i = 0; i < product.variants.length; i++) {
        var raw = String(product.variants[i]['option' + (idx + 1)] || '');
        if (raw && candidates.indexOf(raw) === -1) candidates.push(raw);
      }
    }

    var rio = (section && section.querySelector(RIO)) || document.querySelector(RIO);
    var group = rio && findOptionGroup(rio, optionName);
    if (group) {
      Array.prototype.forEach.call(group.querySelectorAll('select option'), function (opt) {
        var t = String(opt.text || opt.value || '').trim();
        if (t && candidates.indexOf(t) === -1) candidates.push(t);
      });
    }

    if (!candidates.length) return valueName;
    return pickBestValue(candidates, valueName) || valueName;
  }

  /* =======================================================================
     Activation — making the app adopt a value
     ======================================================================= */

  function getNodeLabel(el) {
    if (!el) return '';
    var data = el.getAttribute && el.getAttribute('data-value');
    if (data && String(data).trim()) return String(data).trim();
    if (el.tagName === 'OPTION') {
      return String(el.text || el.value || '')
        .replace(/\s+/g, ' ')
        .trim();
    }
    var aria = el.getAttribute && el.getAttribute('aria-label');
    if (aria && String(aria).trim() && String(aria).length < 80) {
      return String(aria).replace(/\s+/g, ' ').trim();
    }
    var title = el.getAttribute && el.getAttribute('title');
    if (title && String(title).trim() && String(title).length < 80) {
      return String(title).replace(/\s+/g, ' ').trim();
    }
    if (el.value && String(el.value).trim() && String(el.value).length < 80) {
      return String(el.value).replace(/\s+/g, ' ').trim();
    }
    var text = String(el.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
    // Wrappers dump the whole list into textContent.
    if (!text || text.length > 80) return '';
    return text;
  }

  function setNativeSelect(sel, optEl) {
    if (!sel || !optEl) return false;
    try {
      // React tracks the last value it wrote, so the native setter has to be
      // used or the change event is swallowed as a no-op.
      var setter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, 'value');
      if (setter && setter.set) setter.set.call(sel, optEl.value);
      else sel.value = optEl.value;
      sel.selectedIndex = optEl.index;
      sel.dispatchEvent(new Event('input', { bubbles: true }));
      sel.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    } catch (err) {
      return false;
    }
  }

  function clickMatchingNode(root, valueName) {
    if (!root || !valueName) return false;
    var nodes = root.querySelectorAll(
      'option, li, button, [role="option"], [role="button"], label, .MuiMenuItem-root, [class*="MenuItem"]'
    );
    var matches = [];
    var labels = [];
    var i;
    for (i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (isOurNode(el)) continue;
      // Never click the select trigger — that only opens the ghost dropdown.
      if (
        el.getAttribute('role') === 'combobox' ||
        el.getAttribute('aria-haspopup') === 'listbox' ||
        (el.classList && el.classList.contains('MuiSelect-select')) ||
        (el.closest &&
          el.closest('.MuiSelect-root') &&
          el.getAttribute('role') !== 'option' &&
          !(el.classList && el.classList.contains('MuiMenuItem-root')))
      ) {
        continue;
      }
      var label = getNodeLabel(el);
      if (!label) continue;
      matches.push(el);
      labels.push(label);
    }

    var best = pickBestValue(labels, valueName);
    if (!best) return false;

    for (i = 0; i < matches.length; i++) {
      if (!valuesEqual(labels[i], best)) continue;
      var node = matches[i];
      try {
        if (node.tagName === 'OPTION') {
          var sel = node.parentElement;
          if (sel && sel.tagName === 'SELECT') return setNativeSelect(sel, node);
        }
        node.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
        node.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
        node.click();
        return true;
      } catch (err) {}
    }
    return false;
  }

  function closeOpenMenus() {
    try {
      document.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'Escape',
          code: 'Escape',
          keyCode: 27,
          which: 27,
          bubbles: true,
          cancelable: true,
        })
      );
    } catch (err) {}

    Array.prototype.forEach.call(
      document.querySelectorAll(
        '.MuiPopover-root, .MuiModal-root, .MuiMenu-root, .MuiPopper-root, div[role="presentation"]'
      ),
      function (el) {
        // Only portal menus — never the app itself.
        var looksLikeMenu = !!(
          el.classList.contains('MuiPopover-root') ||
          el.classList.contains('MuiModal-root') ||
          el.classList.contains('MuiMenu-root') ||
          el.classList.contains('MuiPopper-root') ||
          el.querySelector('[role="listbox"], .MuiMenu-list, .MuiMenuItem-root, [class*="MenuItem"]')
        );
        if (!looksLikeMenu) return;
        try {
          el.style.setProperty('display', 'none', 'important');
          el.style.setProperty('visibility', 'hidden', 'important');
          el.setAttribute('aria-hidden', 'true');
        } catch (err2) {}
      }
    );
  }

  function withHiddenMenus(fn) {
    document.body.classList.add('sh-rio-picking');
    var result = false;
    try {
      result = !!fn();
    } finally {
      closeOpenMenus();
      window.setTimeout(function () {
        closeOpenMenus();
        document.body.classList.remove('sh-rio-picking');
      }, 80);
      window.setTimeout(closeOpenMenus, 200);
    }
    return result;
  }

  /** Calls the React onChange the app attached, found through the fiber. */
  function fireReactChange(groupRoot, valueName) {
    if (!groupRoot || !valueName) return false;
    var el =
      groupRoot.querySelector(
        '[role="combobox"], .MuiSelect-select, [class*="Select-select"], select'
      ) || groupRoot;

    var depth;
    for (depth = 0; depth < 10 && el; depth++) {
      var keys = Object.keys(el);
      var k;
      for (k = 0; k < keys.length; k++) {
        var key = keys[k];
        if (key.indexOf('__reactFiber$') === 0 || key.indexOf('__reactInternalInstance$') === 0) {
          var f = el[key];
          var hops;
          for (hops = 0; hops < 20 && f; hops++) {
            var props = f.memoizedProps || f.pendingProps;
            if (props && typeof props.onChange === 'function') {
              try {
                props.onChange(
                  {
                    target: { value: valueName },
                    currentTarget: { value: valueName },
                    bubbles: true,
                  },
                  valueName
                );
                return true;
              } catch (err) {}
            }
            f = f.return;
          }
        }
        if (key.indexOf('__reactProps$') === 0) {
          var rp = el[key];
          if (rp && typeof rp.onChange === 'function') {
            try {
              rp.onChange({
                target: { value: valueName },
                currentTarget: { value: valueName },
              });
              return true;
            } catch (err2) {}
          }
        }
      }
      el = el.parentElement;
    }
    return false;
  }

  /** Swaps the variant on the form directly, which needs no app cooperation. */
  function activateViaVariant(optionName, valueName, section) {
    if (!optionName || !valueName) return false;
    var product = getProductData();
    var current = getCurrentVariant(section);
    if (!product || !current || !Array.isArray(product.variants)) return false;

    var optionNames = (product.options || []).map(function (o) {
      return typeof o === 'string' ? o : o && o.name;
    });
    var idx = findShopifyOptionIndex(optionName);
    if (idx < 0) return false;

    var resolved = resolveShopifyValue(optionName, valueName, section);
    var desired = [];
    var i;
    for (i = 0; i < optionNames.length; i++) {
      desired.push(i === idx ? resolved : String(current['option' + (i + 1)] || ''));
    }

    var match = null;
    for (i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      var ok = true;
      var j;
      for (j = 0; j < desired.length; j++) {
        if (!valuesEqual(String(v['option' + (j + 1)] || ''), desired[j])) {
          ok = false;
          break;
        }
      }
      if (ok) {
        match = v;
        break;
      }
    }
    if (!match) return false;

    var form = getForm(section);
    if (!form) return false;
    var idInput = form.querySelector('[name="id"]');
    if (!idInput) return false;

    if (String(idInput.value) !== String(match.id)) {
      idInput.value = String(match.id);
      try {
        idInput.dispatchEvent(new Event('input', { bubbles: true }));
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (err) {}
    }

    try {
      form.dispatchEvent(
        new CustomEvent('variant:changed', { bubbles: true, detail: { variant: match } })
      );
    } catch (err2) {}

    return true;
  }

  function pickFromOpenMenus(valueName) {
    var menus = document.querySelectorAll(
      '[role="listbox"], .MuiMenu-list, .MuiPopover-root [role="menu"], ul[role="listbox"]'
    );
    var m;
    for (m = 0; m < menus.length; m++) {
      if (clickMatchingNode(menus[m], valueName)) return true;
    }
    return clickMatchingNode(document, valueName);
  }

  function activateValue(groupRoot, valueName, optionName, section) {
    if (!groupRoot || !valueName) return false;

    var resolved = resolveShopifyValue(optionName, valueName, section);

    function applyToSelects(wanted) {
      var selects = groupRoot.querySelectorAll('select');
      var s;
      for (s = 0; s < selects.length; s++) {
        var sel = selects[s];
        var labels = [];
        var o;
        for (o = 0; o < sel.options.length; o++) {
          labels.push(String(sel.options[o].text || sel.options[o].value || '').trim());
        }
        var best = pickBestValue(labels, wanted);
        if (!best) continue;
        for (o = 0; o < sel.options.length; o++) {
          if (valuesEqual(sel.options[o].text || sel.options[o].value || '', best)) {
            if (setNativeSelect(sel, sel.options[o])) return true;
          }
        }
      }
      return false;
    }

    // 1) Shopify variant swap — no dropdown involved at all.
    if (activateViaVariant(optionName, valueName, section)) {
      fireReactChange(groupRoot, resolved);
      fireReactChange(groupRoot, valueName);
      applyToSelects(resolved);
      applyToSelects(valueName);
      closeOpenMenus();
      return true;
    }

    // 2) Native select or React onChange — still nothing visible opens.
    if (applyToSelects(resolved) || applyToSelects(valueName)) {
      fireReactChange(groupRoot, resolved);
      closeOpenMenus();
      return true;
    }
    if (fireReactChange(groupRoot, resolved) || fireReactChange(groupRoot, valueName)) {
      closeOpenMenus();
      return true;
    }

    // 3) Option nodes already rendered in the group.
    if (clickMatchingNode(groupRoot, resolved) || clickMatchingNode(groupRoot, valueName)) {
      closeOpenMenus();
      return true;
    }

    // 4) Last resort: open the real menu invisibly, pick, force it closed.
    return withHiddenMenus(function () {
      var trigger = groupRoot.querySelector(
        '[aria-haspopup="listbox"], [role="combobox"], .MuiSelect-select, [class*="Select-select"]'
      );
      if (trigger && !isOurNode(trigger)) {
        try {
          trigger.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
          trigger.click();
        } catch (err) {}
        if (pickFromOpenMenus(resolved) || pickFromOpenMenus(valueName)) return true;
      }

      if (applyToSelects(resolved) || applyToSelects(valueName)) return true;

      var rio = document.querySelector(RIO);
      if (
        rio &&
        rio !== groupRoot &&
        (clickMatchingNode(rio, resolved) || clickMatchingNode(rio, valueName))
      ) {
        return true;
      }
      return false;
    });
  }

  /* =======================================================================
     Availability
     ======================================================================= */

  function isNativeValueUnavailable(groupRoot, valueName) {
    if (!groupRoot || !valueName) return null;
    var scopes = groupScopes(groupRoot);
    var s;
    var n;
    for (s = 0; s < scopes.length; s++) {
      var nodes = scopes[s].querySelectorAll(
        'button, [role="button"], [role="option"], [role="radio"], label'
      );
      for (n = 0; n < nodes.length; n++) {
        var el = nodes[n];
        if (isOurNode(el)) continue;
        var label = getNodeLabel(el);
        if (!label || !valuesEqual(label, valueName)) continue;
        if (
          el.disabled ||
          el.getAttribute('aria-disabled') === 'true' ||
          (el.classList &&
            (el.classList.contains('Mui-disabled') || el.classList.contains('is-unavailable'))) ||
          /disabled|unavailable|sold[-_]?out|out[-_]?of[-_]?stock|strikethrough/i.test(
            el.className || ''
          )
        ) {
          return true;
        }
        try {
          var st = window.getComputedStyle(el);
          if (
            st.textDecorationLine === 'line-through' ||
            (st.textDecoration && st.textDecoration.indexOf('line-through') !== -1)
          ) {
            return true;
          }
        } catch (err) {}
        return false;
      }
    }
    return null;
  }

  function isShopifyValueAvailable(optionName, valueName, section) {
    var product = getProductData();
    var current = getCurrentVariant(section);
    if (!product || !current || !Array.isArray(product.variants)) return true;

    var idx = findShopifyOptionIndex(optionName);
    if (idx < 0) return true;

    var optionNames = (product.options || []).map(function (o) {
      return typeof o === 'string' ? o : o && o.name;
    });
    var resolved = resolveShopifyValue(optionName, valueName, section) || valueName;
    var i;
    var j;
    for (i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      var ok = true;
      for (j = 0; j < optionNames.length; j++) {
        var wanted = j === idx ? resolved : String(current['option' + (j + 1)] || '');
        if (!valuesEqual(String(v['option' + (j + 1)] || ''), wanted)) {
          ok = false;
          break;
        }
      }
      if (ok) return v.available !== false;
    }
    return false;
  }

  /**
   * Copy whatever the app already painted, then check Shopify. Rebuilding a
   * pill row without reading the live nodes is how stock marks get dropped.
   */
  function isValueUnavailable(optionName, valueName, section, groupRoot) {
    var native = isNativeValueUnavailable(groupRoot, valueName);
    if (native === true) return true;

    var product = getProductData();
    if (product && Array.isArray(product.variants) && product.variants.length) {
      if (!isShopifyValueAvailable(optionName, valueName, section)) return true;
    }
    return native === true;
  }

  function applyAvailability(el, unavailable) {
    if (!el) return;
    el.classList.toggle('is-unavailable', !!unavailable);
    el.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
  }

  /* =======================================================================
     Hiding the groups we have replaced
     ======================================================================= */

  function hideNode(el) {
    if (!el) return;
    el.classList.add(HIDDEN_CLASS);
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute(HIDDEN_ATTR, '1');
  }

  function unhideNode(el) {
    if (!el) return;
    el.classList.remove(HIDDEN_CLASS);
    el.removeAttribute('aria-hidden');
    el.removeAttribute(HIDDEN_ATTR);
  }

  function hideGroup(groupRoot) {
    if (!groupRoot) return;
    var start = getHideRoot(groupRoot);
    hideNode(start);

    var node = start.nextElementSibling;
    while (node) {
      if (node.getAttribute && node.getAttribute(HIDDEN_ATTR) === '1') {
        node = node.nextElementSibling;
        continue;
      }
      if (node.querySelectorAll && node.querySelectorAll(TITLE_SEL).length) break;
      hideNode(node);
      node = node.nextElementSibling;
    }
  }

  function unhideGroup(groupRoot) {
    if (!groupRoot) return;
    var start = getHideRoot(groupRoot) || groupRoot;
    unhideNode(start);
    var node = start.nextElementSibling;
    while (node) {
      if (node.querySelectorAll && node.querySelectorAll(TITLE_SEL).length) break;
      unhideNode(node);
      node = node.nextElementSibling;
    }
  }

  /** Re-applied after the app re-renders its options on a variant change. */
  function ensureNativeGroupsHidden(section) {
    section = section || document.querySelector(SECTION);
    if (!section) return;

    var rio = section.querySelector(RIO);
    var host = section.querySelector('.' + PILLS);
    var fonts = section.querySelector('.' + FONTS);
    if (!rio || (!host && !fonts)) return;

    function hideForHost(h) {
      if (!h) return;
      Array.prototype.forEach.call(h.querySelectorAll('[' + OPT_ATTR + ']'), function (block) {
        var optionName = block.getAttribute(OPT_ATTR) || '';
        if (!optionName) return;
        var group = findOptionGroup(rio, optionName);
        if (group) hideGroup(group);
      });
    }

    hideForHost(host);
    hideForHost(fonts);

    if (!fonts) return;

    // Font options are matched by value as well: the app sometimes renders
    // their tiles outside the titled group, where findOptionGroup misses them.
    Array.prototype.forEach.call(fonts.querySelectorAll('[' + OPT_ATTR + ']'), function (block) {
      var optionName = block.getAttribute(OPT_ATTR) || '';
      if (!optionName) return;

      var fontValues = [];
      Array.prototype.forEach.call(block.querySelectorAll('[' + VALUE_ATTR + ']'), function (tile) {
        var v = tile.getAttribute(VALUE_ATTR);
        if (v) fontValues.push(v);
      });
      if (!fontValues.length) return;

      Array.prototype.forEach.call(
        rio.querySelectorAll('button, [role="button"], [role="option"], [role="radio"]'),
        function (el) {
          if (isOurNode(el)) return;
          var label = getNodeLabel(el);
          if (!label) return;
          var hit = false;
          var i;
          for (i = 0; i < fontValues.length; i++) {
            if (valuesEqual(label, fontValues[i])) {
              hit = true;
              break;
            }
          }
          if (!hit) return;

          // Only if the nearest title is this option's — a value name can
          // legitimately repeat across options.
          var nearestTitle = null;
          var walk = el;
          while (walk && walk !== rio) {
            var titles = walk.querySelectorAll ? walk.querySelectorAll(TITLE_SEL) : [];
            if (titles.length === 1) {
              nearestTitle = titles[0];
              break;
            }
            if (titles.length > 1) break;
            walk = walk.parentElement;
          }
          if (nearestTitle && !titleMatchesOption(nearestTitle.textContent || '', optionName)) {
            return;
          }

          var wrap = el.closest('[class*="Grid-item"]') || el.closest('[class*="MuiGrid-item"]') || el;
          if (wrap && wrap.querySelectorAll && wrap.querySelectorAll(TITLE_SEL).length > 1) {
            wrap = el;
          }
          if (wrap && rio.contains(wrap) && wrap !== rio) hideGroup(wrap);
        }
      );
    });
  }

  /**
   * The app remounts its options on its own schedule, which un-hides the
   * groups we replaced and shows both UIs at once. Nothing we can subscribe
   * to signals it, so this re-hides on the events that usually precede it and
   * polls as a backstop.
   */
  function startHideKeepAlive(section) {
    if (!section) return;

    if (!section.__shRioHideBound) {
      section.__shRioHideBound = true;

      var form = getForm(section);
      if (form) {
        form.addEventListener('variant:changed', function () {
          ensureNativeGroupsHidden(section);
          [0, 120, 400].forEach(function (delay) {
            window.setTimeout(function () {
              ensureNativeGroupsHidden(section);
            }, delay);
          });
          startHideKeepAlive(section);
        });
      }

      document.addEventListener(
        'click',
        function () {
          if (section.querySelector('.' + PILLS) || section.querySelector('.' + FONTS)) {
            window.setTimeout(function () {
              ensureNativeGroupsHidden(section);
            }, 50);
          }
        },
        true
      );
    }

    if (section.__shRioHideTimer) return;

    var hidePasses = 0;
    section.__shRioHideTimer = window.setInterval(function () {
      hidePasses += 1;
      if (
        hidePasses > 50 ||
        (!section.querySelector('.' + PILLS) && !section.querySelector('.' + FONTS))
      ) {
        window.clearInterval(section.__shRioHideTimer);
        section.__shRioHideTimer = null;
        return;
      }
      ensureNativeGroupsHidden(section);
      applyFormOrder(section);
    }, 400);
  }

  /* =======================================================================
     Pills
     ======================================================================= */

  function getExtrasAnchor(section, rio) {
    return section.querySelector('.' + FONTS) || rio;
  }

  function buildPillsHost(pillOptions, section) {
    var rio = section.querySelector(RIO);
    if (!rio || !rio.parentNode) return null;

    var host = section.querySelector('.' + PILLS);
    var anchor = getExtrasAnchor(section, rio);
    if (!host) {
      host = document.createElement('div');
      host.className = PILLS;
      anchor.insertAdjacentElement('afterend', host);
    } else if (anchor.nextElementSibling !== host) {
      anchor.insertAdjacentElement('afterend', host);
    }

    host.innerHTML = '';
    host.__shRioPillState = host.__shRioPillState || {};

    pillOptions.forEach(function (opt) {
      var optionName = opt.name || opt.id || '';
      if (!optionName) return;

      var groupRoot = findOptionGroup(rio, optionName);
      if (groupRoot) hideGroup(groupRoot);

      var values = getCanonicalValues(optionName, section, opt.values || []);
      if (!values.length) return;

      var selected =
        getSelectedValue(optionName, section) || host.__shRioPillState[optionName] || values[0];
      if (values.indexOf(selected) === -1) {
        selected = pickBestValue(values, selected) || values[0];
      }
      host.__shRioPillState[optionName] = selected;

      var block = document.createElement('div');
      block.className = PILLS + '__group';
      block.setAttribute(OPT_ATTR, optionName);

      var label = document.createElement('p');
      label.className = PILLS + '__label';
      label.innerHTML =
        '<strong>' +
        escapeHtml(optionName) +
        ':</strong> <span class="' +
        PILLS +
        '__value">' +
        escapeHtml(selected) +
        '</span>';

      var list = document.createElement('div');
      list.className = PILLS + '__list';
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', optionName);

      values.forEach(function (valueName) {
        var isOn = valuesEqual(valueName, selected);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = PILLS + '__pill' + (isOn ? ' is-active' : '');
        btn.setAttribute('role', 'option');
        btn.setAttribute('aria-selected', isOn ? 'true' : 'false');
        btn.textContent = valueName;
        applyAvailability(btn, isValueUnavailable(optionName, valueName, section, groupRoot));

        btn.addEventListener('click', function () {
          var group = findOptionGroup(rio, optionName) || groupRoot;
          var targetRoot = group || rio;
          host.__shRioPillPending = host.__shRioPillPending || {};
          host.__shRioPillPending[optionName] = valueName;

          function applySelected(finalValue) {
            var matched = pickBestValue(values, finalValue || valueName) || valueName;
            host.__shRioPillState[optionName] = matched;
            var valueEl = block.querySelector('.' + PILLS + '__value');
            if (valueEl) valueEl.textContent = matched;
            Array.prototype.forEach.call(
              list.querySelectorAll('.' + PILLS + '__pill'),
              function (pill) {
                var on = valuesEqual(pill.textContent, matched);
                pill.classList.toggle('is-active', on);
                pill.setAttribute('aria-selected', on ? 'true' : 'false');
              }
            );
            syncPillAvailability(section);
          }

          function ensureActivated() {
            var actual = getSelectedValue(optionName, section);
            if (!valuesEqual(pickBestValue(values, actual) || actual, valueName)) {
              // Quiet retries only — never re-open the native dropdown here.
              activateViaVariant(optionName, valueName, section);
              fireReactChange(targetRoot, valueName);
            }
            // The clicked pill stays the source of truth so a near-miss
            // rematch cannot snap the selection onto a sibling value.
            applySelected(valueName);
            closeOpenMenus();
          }

          if (host.__shRioPillTimers) {
            host.__shRioPillTimers.forEach(function (id) {
              window.clearTimeout(id);
            });
          }

          activateValue(targetRoot, valueName, optionName, section);
          applySelected(valueName);
          closeOpenMenus();

          host.__shRioPillTimers = [
            window.setTimeout(ensureActivated, 30),
            window.setTimeout(ensureActivated, 120),
            window.setTimeout(ensureActivated, 280),
            window.setTimeout(function () {
              if (
                host.__shRioPillPending &&
                host.__shRioPillPending[optionName] === valueName
              ) {
                delete host.__shRioPillPending[optionName];
              }
            }, 400),
          ];
        });

        list.appendChild(btn);
      });

      block.appendChild(label);
      block.appendChild(list);
      host.appendChild(block);
    });

    return host;
  }

  function syncPillAvailability(section) {
    var host = section && section.querySelector('.' + PILLS);
    if (!host) return;
    var rio = (section && section.querySelector(RIO)) || document.querySelector(RIO);

    Array.prototype.forEach.call(host.querySelectorAll('.' + PILLS + '__group'), function (block) {
      var optionName = block.getAttribute(OPT_ATTR) || '';
      if (!optionName) return;
      var groupRoot = rio && findOptionGroup(rio, optionName);
      Array.prototype.forEach.call(block.querySelectorAll('.' + PILLS + '__pill'), function (pill) {
        var value = String(pill.textContent || '')
          .replace(/\s+/g, ' ')
          .trim();
        applyAvailability(pill, isValueUnavailable(optionName, value, section, groupRoot));
      });
    });
  }

  function syncPills(section) {
    var host = section.querySelector('.' + PILLS);
    if (!host) return;

    ensureNativeGroupsHidden(section);

    Array.prototype.forEach.call(host.querySelectorAll('.' + PILLS + '__group'), function (block) {
      var optionName = block.getAttribute(OPT_ATTR) || '';
      if (!optionName) return;

      var selected = getSelectedValue(optionName, section);
      if (host.__shRioPillPending && host.__shRioPillPending[optionName]) {
        selected = host.__shRioPillPending[optionName];
      }
      if (!selected) return;

      var pills = block.querySelectorAll('.' + PILLS + '__pill');
      var pillValues = [];
      Array.prototype.forEach.call(pills, function (pill) {
        pillValues.push(String(pill.textContent || '').replace(/\s+/g, ' ').trim());
      });
      var matched = pickBestValue(pillValues, selected);
      if (!matched) return;

      host.__shRioPillState = host.__shRioPillState || {};
      host.__shRioPillState[optionName] = matched;

      var valueEl = block.querySelector('.' + PILLS + '__value');
      if (valueEl) valueEl.textContent = matched;

      Array.prototype.forEach.call(pills, function (pill) {
        var on = valuesEqual(pill.textContent, matched);
        pill.classList.toggle('is-active', on);
        pill.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    });

    syncPillAvailability(section);
  }

  /** Rebuild only when the option set actually changed, not on every pass. */
  function pillSignature(options, section) {
    return (options || [])
      .map(function (opt) {
        return (
          normalizeLabel((opt && (opt.name || opt.id)) || '') +
          '=' +
          countOptionValues(opt, section)
        );
      })
      .join('|');
  }

  function placePills(section) {
    section = section || document.querySelector(SECTION);
    if (!section) return false;

    var rio = section.querySelector(RIO);
    if (!rio || !rio.querySelector(TITLE_SEL)) return false;

    var optionsData = getOptionsData();
    if (!optionsData) return false;

    var pillOptions = getPillOptions(optionsData, rio);
    var existing = section.querySelector('.' + PILLS);

    if (!pillOptions.length) {
      if (existing) {
        // An option can drop out mid-session (a variant change can leave a
        // group with too many values). Give those back rather than binning
        // the whole host and the options that still want pills.
        var keepCount = 0;
        Array.prototype.forEach.call(
          existing.querySelectorAll('[' + OPT_ATTR + ']'),
          function (block) {
            var optionName = block.getAttribute(OPT_ATTR) || '';
            if (optionName && stillWantsPills(optionName, optionsData, section)) {
              keepCount += 1;
              return;
            }
            var group = findOptionGroup(rio, optionName);
            if (group) unhideGroup(group);
            if (block.parentNode) block.parentNode.removeChild(block);
          }
        );
        if (keepCount && existing.querySelector('[' + OPT_ATTR + ']')) {
          ensureNativeGroupsHidden(section);
          syncPills(section);
          startHideKeepAlive(section);
          watchRioInner(section);
          return true;
        }
        existing.remove();
      }
      return true;
    }

    var sig = pillSignature(pillOptions, section);
    if (existing && existing.__shRioPillSig === sig) {
      ensureNativeGroupsHidden(section);
      syncPills(section);
      startHideKeepAlive(section);
      watchRioInner(section);
      return true;
    }

    buildPillsHost(pillOptions, section);
    existing = section.querySelector('.' + PILLS);
    if (existing) existing.__shRioPillSig = sig;

    ensureNativeGroupsHidden(section);
    syncPills(section);
    startHideKeepAlive(section);
    applyFormOrder(section);
    watchFormOrder(section);
    watchRioInner(section);
    return true;
  }

  function initPills() {
    var section = document.querySelector(SECTION);
    if (!section) return;
    ensureProductJson();

    function bindSync() {
      if (section.__shRioPillsBound) return;
      section.__shRioPillsBound = true;
      var form = getForm(section);
      if (form) {
        form.addEventListener('variant:changed', function () {
          placePills(section);
        });
      }
    }

    if (placePills(section)) {
      bindSync();
      markFeatureReady('pills');
      return;
    }

    bindSync();
    if (window.__shRioPillsPolling) return;
    window.__shRioPillsPolling = true;
    pollUntil(
      function () {
        return placePills(section);
      },
      { interval: 50, maxTries: 80, onDone: function () { markFeatureReady('pills'); } }
    );
  }

  /* =======================================================================
     Font tiles
     ======================================================================= */

  function dedupeHosts(className) {
    var all = document.querySelectorAll('.' + className);
    if (all.length < 2) return all[0] || null;
    var keep = all[0];
    var i;
    for (i = 1; i < all.length; i++) {
      if (all[i].parentNode) all[i].parentNode.removeChild(all[i]);
    }
    return keep;
  }

  function rioHasOtherOptions(rio) {
    if (!rio) return false;
    var i;
    var data = getOptionsData();
    if (data && Array.isArray(data.options) && data.options.length) {
      for (i = 0; i < data.options.length; i++) {
        if (data.options[i] && !isFontOption(data.options[i])) return true;
      }
      return false;
    }
    var titles = rio.querySelectorAll(TITLE_SEL);
    for (i = 0; i < titles.length; i++) {
      if (isOurNode(titles[i]) || isHiddenByUs(titles[i])) continue;
      if (!/schriftart/i.test(titles[i].textContent || '')) return true;
    }
    var swatches = rio.querySelectorAll('[role="button"][aria-label]');
    for (i = 0; i < swatches.length; i++) {
      if (isOurNode(swatches[i]) || isHiddenByUs(swatches[i])) continue;
      if (!isAppImageSwatch(swatches[i])) continue;
      var label = swatches[i].getAttribute('aria-label') || '';
      if (/schrift|ohne\s*name/i.test(label)) continue;
      return true;
    }
    return false;
  }

  function hideNativeFontGroups(section) {
    section = section || document.querySelector(SECTION);
    var rio = section && section.querySelector(RIO);
    var fonts = document.querySelector('.' + FONTS);
    if (!rio || !fonts) return;

    ensureNativeGroupsHidden(section);

    Array.prototype.forEach.call(rio.querySelectorAll(TITLE_SEL), function (title) {
      if (isOurNode(title) || isHiddenByUs(title)) return;
      if (!/schriftart/i.test(title.textContent || '')) return;
      var group = findOptionGroup(rio, title.textContent || '');
      if (group && group !== rio && group.querySelectorAll(TITLE_SEL).length <= 1) {
        hideGroup(group);
      }
    });

    // Schriftart-only ProductWiz mounts have no title class we can match.
    // The leftover row is the whole .rio-options node.
    if (!rioHasOtherOptions(rio)) {
      hideNode(rio);
      rio.classList.add('sh-rio-fonts-replaced');
    }
  }

  function buildFontTilesHost(fontOptions, section) {
    var rio = section.querySelector(RIO);
    if (!rio || !rio.parentNode) return null;

    var host = dedupeHosts(FONTS) || section.querySelector('.' + FONTS);
    if (!host) {
      host = document.createElement('div');
      host.className = FONTS;
      rio.insertAdjacentElement('afterend', host);
    } else if (rio.nextElementSibling !== host) {
      rio.insertAdjacentElement('afterend', host);
    }

    // Pills anchor off the font host, so keep them after it.
    var pills = section.querySelector('.' + PILLS);
    if (pills && host.nextElementSibling !== pills) {
      host.insertAdjacentElement('afterend', pills);
    }

    host.innerHTML = '';
    host.__shRioFontState = host.__shRioFontState || {};

    fontOptions.forEach(function (opt) {
      var optionName = opt.name || opt.id || '';
      if (!optionName) return;

      var groupRoot = findOptionGroup(rio, optionName);
      if (groupRoot) hideGroup(groupRoot);

      var metaByKey = {};
      (opt.values || []).forEach(function (v) {
        var meta = getFontValueMeta(v);
        if (!meta.name) return;
        metaByKey[normalizeLabel(meta.name)] = meta;
      });

      // Canonical names decide what shows; the config only supplies images.
      var metas = getCanonicalValues(optionName, section, opt.values || []).map(function (name) {
        return metaByKey[normalizeLabel(name)] || { name: name, img: '' };
      });
      if (!metas.length) return;

      var valueNames = metas.map(function (m) {
        return m.name;
      });
      var selected =
        getSelectedValue(optionName, section) ||
        host.__shRioFontState[optionName] ||
        valueNames[0];
      if (valueNames.indexOf(selected) === -1) {
        selected = pickBestValue(valueNames, selected) || valueNames[0];
      }
      host.__shRioFontState[optionName] = selected;

      var block = document.createElement('div');
      block.className = FONTS + '__group';
      block.setAttribute(OPT_ATTR, optionName);

      var label = document.createElement('p');
      label.className = FONTS + '__label';
      label.textContent = optionName;

      var list = document.createElement('div');
      list.className = FONTS + '__list';
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', optionName);

      metas.forEach(function (meta) {
        var isOn = meta.name === selected;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = FONTS + '__tile' + (isOn ? ' is-active' : '');
        btn.setAttribute('role', 'option');
        btn.setAttribute('aria-selected', isOn ? 'true' : 'false');
        btn.setAttribute('aria-label', meta.name);
        btn.setAttribute(VALUE_ATTR, meta.name);
        applyAvailability(btn, isValueUnavailable(optionName, meta.name, section, groupRoot));

        if (meta.img) {
          btn.innerHTML =
            '<img class="' +
            FONTS +
            '__img" src="' +
            escapeHtml(meta.img) +
            '" alt="' +
            escapeHtml(meta.name) +
            '" width="72" height="72" loading="eager" fetchpriority="high" decoding="async">';
        } else {
          btn.innerHTML =
            '<span class="' + FONTS + '__text">' + escapeHtml(meta.name) + '</span>';
        }

        btn.addEventListener('click', function () {
          host.__shRioFontState[optionName] = meta.name;

          Array.prototype.forEach.call(
            list.querySelectorAll('.' + FONTS + '__tile'),
            function (tile) {
              var on = tile.getAttribute(VALUE_ATTR) === meta.name;
              tile.classList.toggle('is-active', on);
              tile.setAttribute('aria-selected', on ? 'true' : 'false');
            }
          );

          var group = findOptionGroup(rio, optionName) || groupRoot;
          activateValue(group || rio, meta.name, optionName, section);
          closeOpenMenus();
          window.setTimeout(closeOpenMenus, 80);
        });

        list.appendChild(btn);
      });

      block.appendChild(label);
      block.appendChild(list);
      host.appendChild(block);
    });

    return host;
  }

  function syncFontTiles(section) {
    var host = section.querySelector('.' + FONTS);
    if (!host) return;

    ensureNativeGroupsHidden(section);

    Array.prototype.forEach.call(host.querySelectorAll('.' + FONTS + '__group'), function (block) {
      var optionName = block.getAttribute(OPT_ATTR) || '';
      if (!optionName) return;
      var selected = getSelectedValue(optionName, section);
      if (!selected) return;

      host.__shRioFontState = host.__shRioFontState || {};
      host.__shRioFontState[optionName] = selected;

      var groupRoot = findOptionGroup(section.querySelector(RIO) || document, optionName);

      Array.prototype.forEach.call(
        block.querySelectorAll('.' + FONTS + '__tile'),
        function (tile) {
          var value = tile.getAttribute(VALUE_ATTR) || '';
          var on = valuesEqual(value, selected);
          tile.classList.toggle('is-active', on);
          tile.setAttribute('aria-selected', on ? 'true' : 'false');
          applyAvailability(tile, isValueUnavailable(optionName, value, section, groupRoot));
        }
      );
    });
  }

  function placeFontTiles(section) {
    section = section || document.querySelector(SECTION);
    if (!section) return false;

    var rio = section.querySelector(RIO);
    if (!rio) return false;

    var optionsData = getOptionsData();
    if (!optionsData) return false;

    var fontOptions = getFontOptions(optionsData);
    if (!fontOptions.length) {
      var existing = section.querySelector('.' + FONTS);
      if (existing) existing.remove();
      return true;
    }

    // Do not wait for the React group to paint — that is what made the
    // tiles sit behind the app mount. Groups are hidden when they appear.
    preloadFontImages(fontOptions);

    var existing = dedupeHosts(FONTS);
    var sig = fontTilesSignature(fontOptions);
    if (existing && existing.__shRioFontSig === sig) {
      hideNativeFontGroups(section);
      syncFontTiles(section);
      startHideKeepAlive(section);
      watchRioInner(section);
      return true;
    }

    buildFontTilesHost(fontOptions, section);
    existing = document.querySelector('.' + FONTS);
    if (existing) existing.__shRioFontSig = sig;
    hideNativeFontGroups(section);
    syncFontTiles(section);
    startHideKeepAlive(section);
    applyFormOrder(section);
    watchFormOrder(section);
    watchRioInner(section);
    ensureProductJson();
    return true;
  }

  function refreshFontImages(section) {
    if (window.__shRioFontAssetPoll) return;
    window.__shRioFontAssetPoll = true;
    pollUntil(
      function () {
        var data = getOptionsData();
        if (!data) return false;
        var opts = getFontOptions(data);
        preloadFontImages(opts);
        placeFontTiles(section);
        return fontImagesResolved(opts);
      },
      { interval: 50, maxTries: 80 }
    );
  }

  function initFontTiles() {
    var section = document.querySelector(SECTION);
    if (!section) return;
    ensureProductJson();

    function bindSync() {
      if (section.__shRioFontsBound) return;
      section.__shRioFontsBound = true;
      var form = getForm(section);
      if (form) {
        form.addEventListener('variant:changed', function () {
          placeFontTiles(section);
          window.setTimeout(function () {
            placeFontTiles(section);
          }, 150);
        });
      }
    }

    if (placeFontTiles(section)) {
      bindSync();
      markFeatureReady('fonts');
      refreshFontImages(section);
      return;
    }

    bindSync();
    if (window.__shRioFontsPolling) return;
    window.__shRioFontsPolling = true;
    pollUntil(
      function () {
        var optionsData = getOptionsData();
        if (optionsData) preloadFontImages(getFontOptions(optionsData));
        return placeFontTiles(section);
      },
      {
        interval: 50,
        maxTries: 80,
        onDone: function () {
          markFeatureReady('fonts');
          refreshFontImages(section);
        },
      }
    );
  }

  /* =======================================================================
     Buy box order

     Both hosts are appended after .rio-options, so the buy box needs
     reordering for the pills to sit under the swatches and for the preview to
     come before the personalisation field.

     This assigns an order to every child rather than to a handful of named
     ones. Naming only some — which is what the variation's CSS did — leaves
     the rest on the initial value of 0, which floats them above everything
     that was named; that is why the quantity stepper and the video button
     ended up above the product options. Starting from the DOM order means
     anything not deliberately moved stays exactly where the theme put it.
     ======================================================================= */

  /**
   * Scoped to whatever currently parents .rio-options rather than to
   * .product-form, because assets/ww-pdp.js re-parents the form's children
   * into a buy box wrapper and the order has to be set on real siblings.
   */
  function getOrderScope(section) {
    var rio = section.querySelector(RIO);
    if (rio && rio.parentElement) return rio.parentElement;
    return getForm(section);
  }

  function hasClass(el, name) {
    return !!(el && el.classList && el.classList.contains(name));
  }

  function indexOfClass(list, name) {
    for (var i = 0; i < list.length; i++) {
      if (hasClass(list[i], name)) return i;
    }
    return -1;
  }

  function lastIndexOfClass(list, name) {
    for (var i = list.length - 1; i >= 0; i--) {
      if (hasClass(list[i], name)) return i;
    }
    return -1;
  }

  function applyFormOrder(section) {
    if (!config.order) return;

    var scope = getOrderScope(section);
    if (!scope) return;

    var kids = Array.prototype.slice.call(scope.children);
    if (!kids.length) return;

    var pills = scope.querySelector('.' + PILLS);
    var fonts = scope.querySelector('.' + FONTS);
    var anchor = scope.querySelector('.sh-rio-anchor');
    var rio = scope.querySelector(RIO);

    // Only direct children can be ordered; a host nested elsewhere is left be.
    if (pills && pills.parentElement !== scope) pills = null;
    if (fonts && fonts.parentElement !== scope) fonts = null;
    if (anchor && anchor.parentElement !== scope) anchor = null;

    var seq = kids.filter(function (el) {
      return el !== pills && el !== fonts;
    });

    function insertAfter(node, target) {
      if (!node) return;
      var at = target ? seq.indexOf(target) : -1;
      if (at === -1) seq.push(node);
      else seq.splice(at + 1, 0, node);
    }

    // The theme prints the preview and the trust copy below the
    // personalisation inputs; both belong above them. Everything up to the buy
    // buttons moves as one group so their relative order survives. Moving
    // them in the DOM would re-run the preview's script, so only the order
    // value changes.
    var firstProp = indexOfClass(seq, 'product-form__line-item-property');
    if (firstProp > -1) {
      var buyAt = indexOfClass(seq, 'product-form__buy-buttons');
      if (buyAt === -1) buyAt = seq.length;

      var movers = [];
      for (var i = firstProp + 1; i < buyAt; i++) {
        if (hasClass(seq[i], 'product-form__custom-liquid')) movers.push(seq[i]);
      }
      if (movers.length) {
        seq = seq.filter(function (el) {
          return movers.indexOf(el) === -1;
        });
        var at = indexOfClass(seq, 'product-form__line-item-property');
        if (at > -1) seq.splice.apply(seq, [at, 0].concat(movers));
      }
    }

    // Sit in the ProductWiz slot (above the preview), not after the
    // name field — that stacked a second Schriftart row under it.
    insertAfter(fonts, rio || anchor);

    // The block marks where the pills go. Without it they stay with the app.
    insertAfter(pills, anchor || rio);

    seq.forEach(function (el, idx) {
      var want = String(idx + 1);
      if (el.style.order !== want) el.style.order = want;
    });
  }

  /**
   * The app, the review widget and PayPal all inject into the buy box after
   * load. An element we have not ordered keeps the initial value of 0, which
   * would float it above everything, so every child change re-runs the pass.
   *
   * The scope is re-checked because assets/ww-pdp.js moves the form's children
   * into a wrapper, which makes the element we were watching the wrong one.
   */
  function watchFormOrder(section) {
    if (typeof MutationObserver === 'undefined') return;

    var scope = getOrderScope(section);
    if (!scope || section.__shRioOrderScope === scope) return;

    if (section.__shRioOrderObserver) section.__shRioOrderObserver.disconnect();
    section.__shRioOrderScope = scope;

    var queued = false;
    var observer = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        // childList only: a remounted .rio-options is a new sibling. That is
        // how pills/tiles still rebuild after the subtree mount watcher stops.
        if (config.pills) placePills(section);
        if (config.fonts) placeFontTiles(section);
        applyFormOrder(section);
        watchFormOrder(section);
        watchRioInner(section);
      });
    });
    observer.observe(scope, { childList: true });
    section.__shRioOrderObserver = observer;
  }

  /**
   * Runs for every product, not just the ones that get pills or font tiles —
   * the preview has to move above the personalisation field either way.
   */
  function initFormOrder() {
    var section = document.querySelector(SECTION);
    if (!section) return;

    applyFormOrder(section);
    watchFormOrder(section);

    // The app mounts late, and the pass has to run again once it is in place.
    pollUntil(
      function () {
        applyFormOrder(section);
        watchFormOrder(section);
        return !!section.querySelector(RIO);
      },
      { interval: 50, maxTries: 80 }
    );
  }

  /* =======================================================================
     Swatch row scroll hints
     ======================================================================= */

  function updateRowHint(row, scroller) {
    if (!row || !scroller) return;
    var maxScroll = scroller.scrollWidth - scroller.clientWidth;
    row.classList.toggle('has-more-right', maxScroll > 2 && scroller.scrollLeft < maxScroll - 2);
  }

  function enhanceRow(scroller) {
    if (!scroller || !scroller.parentElement) return;
    var row = scroller.parentElement;
    row.classList.add(ROW);

    if (!scroller.__shRioRowBound) {
      scroller.__shRioRowBound = true;

      var sync = function () {
        updateRowHint(row, scroller);
      };

      scroller.addEventListener('scroll', sync, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(sync).observe(scroller);
      }

      // Swatch images change the scroll width as they land.
      Array.prototype.forEach.call(scroller.querySelectorAll('img'), function (img) {
        if (!img.complete) img.addEventListener('load', sync);
      });
    }

    updateRowHint(row, scroller);
  }

  function enhanceAllRows() {
    var found = false;
    Array.prototype.forEach.call(document.querySelectorAll(RIO), function (rio) {
      Array.prototype.forEach.call(rio.querySelectorAll('.rio-hide-scrollbar'), function (scroller) {
        // Value rows carry data-option / aria-label; other scrollers do not.
        if (!scroller.getAttribute('data-option') && !scroller.getAttribute('aria-label')) return;
        found = true;
        enhanceRow(scroller);
      });
    });
    return found;
  }

  function initScrollHints() {
    pollUntil(enhanceAllRows, { interval: 50, maxTries: 80 });

    if (!window.__shRioRowsResizeBound) {
      window.__shRioRowsResizeBound = true;
      window.addEventListener('resize', enhanceAllRows);
    }

    if (window.__shRioRowsTimer) return;
    var passes = 0;
    window.__shRioRowsTimer = window.setInterval(function () {
      enhanceAllRows();
      passes += 1;
      if (passes > 50) {
        window.clearInterval(window.__shRioRowsTimer);
        window.__shRioRowsTimer = null;
      }
    }, 400);
  }

  /* =======================================================================
     Image swatch selected border

     ProductWiz paints selection as a wine border on [role=button], not
     aria-selected. On a remount it sometimes leaves that border on the
     previous swatch as well. We keep exactly one highlight per row.
     ======================================================================= */

  var SWATCH_BORDER_ON = 'rgb(95, 6, 45)';
  var SWATCH_BORDER_OFF = 'rgb(220, 220, 220)';

  function isAppImageSwatch(el) {
    if (!el || isOurNode(el)) return false;
    if (isHiddenByUs(el)) return false;
    var cls = el.className ? String(el.className) : '';
    if (!/rio-swatch-border/i.test(cls)) return false;
    var label = el.getAttribute('aria-label') || '';
    if (!label || /^scroll/i.test(label)) return false;
    return true;
  }

  function swatchGroupKey(el) {
    var scroller = el.closest && el.closest('[data-option]');
    if (scroller) {
      var fromData = scroller.getAttribute('data-option') || '';
      if (fromData) return fromData;
    }

    var node = el;
    var i;
    for (i = 0; i < 12 && node; i++) {
      if (node.querySelectorAll && node.querySelectorAll(TITLE_SEL).length === 1) {
        var title = node.querySelector(TITLE_SEL);
        if (title) {
          return String(title.textContent || '')
            .split(':')[0]
            .replace(/\s+/g, ' ')
            .trim();
        }
      }
      node = node.parentElement;
    }
    return 'swatch';
  }

  function applySwatchHighlight(el, on) {
    el.style.setProperty('border-color', on ? SWATCH_BORDER_ON : SWATCH_BORDER_OFF, 'important');
    el.style.setProperty('border-style', 'solid', 'important');
    el.style.setProperty('border-width', '2px', 'important');
  }

  function rememberSwatchPick(section, el) {
    if (!section || !el) return;
    section.__shRioSwatchPick = {
      label: el.getAttribute('aria-label') || '',
      at: Date.now(),
    };
  }

  function recentSwatchPick(section) {
    var pick = section && section.__shRioSwatchPick;
    if (!pick || !pick.label) return '';
    if (Date.now() - pick.at > 1200) return '';
    return pick.label;
  }

  function syncAppSwatches(section, preferred) {
    section = section || document.querySelector(SECTION);
    var rio = section && section.querySelector(RIO);
    if (!rio) return;

    var preferredLabel = '';
    if (preferred) preferredLabel = preferred.getAttribute('aria-label') || '';
    if (!preferredLabel) preferredLabel = recentSwatchPick(section);

    var groups = {};
    Array.prototype.forEach.call(rio.querySelectorAll('[role="button"][aria-label]'), function (el) {
      if (!isAppImageSwatch(el)) return;
      var key = swatchGroupKey(el);
      if (!groups[key]) groups[key] = [];
      groups[key].push(el);
    });

    Object.keys(groups).forEach(function (key) {
      var list = groups[key];
      var labels = list.map(function (el) {
        return el.getAttribute('aria-label') || '';
      });
      var selected = '';
      if (preferredLabel && pickBestValue(labels, preferredLabel)) {
        selected = preferredLabel;
      }
      if (!selected) selected = getSelectedValue(key, section);

      var matched = selected ? pickBestValue(labels, selected) : '';

      list.forEach(function (el) {
        var label = el.getAttribute('aria-label') || '';
        applySwatchHighlight(el, !!(matched && valuesEqual(label, matched)));
      });
    });
  }

  function watchAppSwatches(section) {
    if (!section || section.__shRioSwatchBound) return;
    section.__shRioSwatchBound = true;

    section.addEventListener(
      'click',
      function (event) {
        var el =
          event.target &&
          event.target.closest &&
          event.target.closest('[role="button"][aria-label]');
        if (!isAppImageSwatch(el)) return;

        rememberSwatchPick(section, el);
        syncAppSwatches(section, el);
        [16, 50, 120].forEach(function (ms) {
          window.setTimeout(function () {
            syncAppSwatches(section, el);
          }, ms);
        });
        window.setTimeout(function () {
          syncAppSwatches(section);
        }, 400);
      },
      true
    );

    var form = getForm(section);
    if (form) {
      form.addEventListener('variant:changed', function () {
        syncAppSwatches(section);
        window.setTimeout(function () {
          syncAppSwatches(section);
        }, 150);
      });
    }
  }

  /* =======================================================================
     Boot
     ======================================================================= */

  /**
   * After the first conversion the form-wide mount watcher stops. ProductWiz
   * still remounts groups *inside* the same .rio-options (or replaces that
   * node). This watcher stays on the current rio node so placePills /
   * placeFontTiles can run again for the rest of the visit.
   */
  function watchRioInner(section) {
    if (!section || typeof MutationObserver === 'undefined') return;

    var rio = section.querySelector(RIO);
    if (!rio) return;
    if (section.__shRioInnerNode === rio) return;

    if (section.__shRioInnerObserver) {
      section.__shRioInnerObserver.disconnect();
      section.__shRioInnerObserver = null;
    }
    section.__shRioInnerNode = rio;

    var timer = 0;
    var observer = new MutationObserver(function () {
      syncAppSwatches(section);
      if (config.fonts) hideNativeFontGroups(section);
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        timer = 0;
        if (config.pills) placePills(section);
        if (config.fonts) placeFontTiles(section);
        syncAppSwatches(section);
      }, 80);
    });
    observer.observe(rio, { childList: true, subtree: true });
    section.__shRioInnerObserver = observer;
  }

  function watchRioMount(section) {
    if (!section || section.__shRioMountWatch || typeof MutationObserver === 'undefined') return;
    section.__shRioMountWatch = true;

    var root = getForm(section) || section;
    var queued = false;
    var observer = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        if (config.pills) placePills(section);
        if (config.fonts) placeFontTiles(section);
        applyFormOrder(section);
        syncAppSwatches(section);
      });
    });
    observer.observe(root, { childList: true, subtree: true });
    section.__shRioMountObserver = observer;
  }

  function init() {
    if (!document.querySelector(SECTION)) return;
    applyBodyClasses();
    var section = document.querySelector(SECTION);
    watchRioMount(section);
    initFormOrder();
    if (config.pills) initPills();
    if (config.fonts) initFontTiles();
    if (config.hints) initScrollHints();
    watchAppSwatches(section);

    // Last resort: never leave .rio-options invisible. Only reveal after a
    // convert pass if the app did mount; if it has not, keep watching.
    setTimeout(function () {
      if (!section.querySelector(RIO)) return;
      if (config.pills) placePills(section);
      if (config.fonts) placeFontTiles(section);
      markRioReady();
    }, 8000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
